/**
 * N2BriefingPage — single daily briefing viewer at /n2/briefings/:date/:variant?
 *
 * Public shell (headline + regime tables + prices + alert counts) renders
 * for everyone. The gated Market Analysis + top candidate detail render
 * only if the caller is a Premier subscriber; otherwise a compact upgrade
 * teaser stands in for those sections.
 *
 * Backend: notches-api-get-briefings (optional-auth). See
 * docs/wiki/decisions/2026-07-28-daily-briefings-strategy.md.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import { PrimaryButton, SecondaryButton, Eyebrow, PageTitle } from './N2Primitives';
import { PurchaseButton } from './PurchaseButton';
import { applyClientSideCorrections } from './briefingsMarkdownTransform';
import { briefingsService, BriefingFull, BriefingVariant } from '../services/briefingsService';

const VARIANT_LABEL: Record<BriefingVariant, string> = {
  morning: 'Morning briefing',
  eod: 'End-of-day briefing',
};

export const N2BriefingPage: React.FC = () => {
  const { date = 'latest', variant: rawVariant } = useParams<{ date?: string; variant?: string }>();
  const variant: BriefingVariant = rawVariant === 'morning' ? 'morning' : 'eod';
  const { token } = useAuth();
  const loc = useLocation();
  // Login/register links carry `?redirect=<current-path>` so the auth flow
  // drops the user back to the same briefing they were reading. Strip
  // leading slash -- Login.tsx prepends '/' to the value, so keeping it
  // here would produce `//n2/...` which the browser reads as a protocol-
  // relative URL to host "n2".
  const authRedirect = encodeURIComponent((loc.pathname + loc.search).replace(/^\/+/, ''));

  const [briefing, setBriefing] = useState<BriefingFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetch = date === 'latest'
      ? briefingsService.latest(variant, token || undefined)
      : briefingsService.get(date, variant, token || undefined);
    fetch
      .then((b) => { if (!cancelled) setBriefing(b); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Failed to load briefing'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, variant, token]);

  return (
    <N2Frame page="briefing">
      <div style={{
        maxWidth: 880, margin: '0 auto', padding: '32px 24px 96px',
      }}>
        <div style={{ marginBottom: 24 }}>
          <Link to="/n2/briefings" style={{ color: palette.ink3, fontSize: 13, textDecoration: 'none' }}>
            ← All briefings
          </Link>
        </div>

        {loading && (
          <div style={{ color: palette.ink3, fontSize: 14 }}>Loading briefing…</div>
        )}
        {error && (
          <div style={{
            padding: '20px', background: palette.paper2, border: `1px solid ${palette.line}`,
            borderRadius: 8, color: palette.ink2,
          }}>
            <strong>Couldn't load briefing.</strong> {error}
          </div>
        )}

        {briefing && !loading && briefing.archiveLocked && (
          <ArchiveLockedCard date={briefing.briefingDate} variant={briefing.variant}
                              redirect={authRedirect} />
        )}

        {briefing && !loading && !briefing.archiveLocked && (() => {
          // Shared promotional-copy corrections (see
          // briefingsMarkdownTransform.ts) run client-side so the
          // corrected shell can be reviewed before the getBriefings
          // Lambda ships. Idempotent — once the Lambda is deployed, no
          // matches remain and `corrected` returns false.
          const publicOut = applyClientSideCorrections(briefing.publicMarkdown);
          const gatedOut = applyClientSideCorrections(briefing.gatedMarkdown);
          const corrected = publicOut.corrected || gatedOut.corrected;
          // Deterministic reconstructions declare themselves via
          // `composerVersion`. Two variants today:
          //   deterministic-fixture:  pilot editorial fixture (Sept 21)
          //   deterministic-replay:   as-of replay (Sept 18, generated
          //                           after its cutoffs from saved
          //                           evidence)
          // Both need the "based on saved evidence" footer; only the
          // replay variant is labeled "As-of replay" in the eyebrow
          // wording (the pilot fixture is editorial-only).
          const composerVersion = typeof briefing.composerVersion === 'string'
            ? briefing.composerVersion : '';
          const isDeterministicFixture =
            composerVersion.startsWith('deterministic-fixture:');
          const isAsOfReplay =
            composerVersion.startsWith('deterministic-replay:');
          const isFullReplay =
            composerVersion.startsWith('deterministic-full-replay:');
          const isDeterministicReconstruction =
            isDeterministicFixture || isAsOfReplay || isFullReplay;
          const hasGatedBody =
            typeof gatedOut.md === 'string' && gatedOut.md.trim().length > 0;
          return (
            <>
              <Eyebrow>
                {VARIANT_LABEL[briefing.variant]} · {briefing.briefingDate}
                {isAsOfReplay && ' · As-of replay'}
              </Eyebrow>

              {/* Public shell — everyone sees this */}
              <MarkdownBlock md={publicOut.md} />

              {/* Gated block: Market analysis + top candidate detail.
                  Hidden entirely when there is neither gated content
                  nor an upsell (deterministic reconstructions ship
                  complete customer prose with no separate cross-asset
                  gated section). */}
              {(briefing.gatedTeaser || hasGatedBody) && (
                <div style={{ marginTop: 40, borderTop: `1px solid ${palette.line}`, paddingTop: 32 }}>
                  {briefing.gatedTeaser ? (
                    <GatedTeaser redirect={authRedirect} />
                  ) : (
                    <MarkdownBlock md={gatedOut.md} />
                  )}
                </div>
              )}

              {/* One follow-up route out of the article, kept outside
                  the frozen report text. Points at the existing
                  researched-opportunities index at /n2/research. */}
              <div style={{ marginTop: 32, fontSize: 14 }}>
                <Link
                  to="/n2/research"
                  style={{ color: palette.goldDeep, textDecoration: 'underline' }}
                >
                  Explore researched opportunities →
                </Link>
              </div>

              <footer style={{
                marginTop: 48, paddingTop: 20, borderTop: `1px solid ${palette.line}`,
                color: palette.ink3, fontSize: 12,
              }}>
                {isDeterministicReconstruction ? (
                  <>
                    Report cutoff <PublicationTimestamp iso={briefing.generatedAt} /> ·{' '}
                    <Link to="/n2/methodology" style={{ color: palette.ink3, textDecoration: 'underline' }}>
                      Methodology
                    </Link>
                    . Based on the saved evidence for the stated cutoff. Observation
                    times and limitations are described in the report.
                  </>
                ) : (
                  <>
                    Published <PublicationTimestamp iso={briefing.generatedAt} /> ·{' '}
                    <Link to="/n2/methodology" style={{ color: palette.ink3, textDecoration: 'underline' }}>
                      Methodology
                    </Link>
                    . Prices, regime, and positioning reflect the values that were live at publication.
                  </>
                )}
                {corrected && (
                  <>
                    <br />
                    <em>
                      Editorial note: shared site promotional copy (Premier upsell text and
                      per-section marker) has been standardized since publication. Analytical
                      content, figures, and observations are preserved verbatim.
                    </em>
                  </>
                )}
              </footer>
            </>
          );
        })()}
      </div>
    </N2Frame>
  );
};

/**
 * Reader-friendly publication timestamp — ALWAYS Eastern time. This is
 * a US-markets product; timing decisions are made in ET, and mixing in
 * the reader's browser timezone (which `toLocaleString(undefined, …)`
 * would do) makes cross-referencing confusing. The tooltip carries the
 * full ET timestamp for hover-verify.
 */
const PublicationTimestamp: React.FC<{ iso: string }> = ({ iso }) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return <>{iso.slice(0, 19).replace('T', ' ')} ET</>;
  const compact = d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: 'America/New_York',
  }) + ' ET';
  const full = d.toLocaleString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
    timeZone: 'America/New_York',
    timeZoneName: 'short',
  });
  return <span title={full}>{compact}</span>;
};

/** Marker consumed by the full-briefing deterministic renderer to
 * request that everything after this line be wrapped in an actual
 * <details><summary> element on the rendered page. Kept as an HTML
 * comment so no other briefing that lacks the marker changes at all
 * (splitting returns the whole content unchanged). */
const EVIDENCE_MARKER = '<!--EVIDENCE-NOTES-COLLAPSE-START-->';

const MarkdownBlock: React.FC<{ md: string }> = ({ md }) => {
  // Split ONCE on the marker so a briefing that doesn't include it
  // renders byte-identically to prior behaviour.
  const idx = md.indexOf(EVIDENCE_MARKER);
  const beforeMd = idx === -1 ? md : md.slice(0, idx);
  const afterMd = idx === -1
    ? null
    : md.slice(idx + EVIDENCE_MARKER.length);
  return (
    <div className="briefing-md" style={{
      fontSize: 15, lineHeight: 1.6, color: palette.ink,
      fontFamily: 'var(--sans)',
    }}>
      <style>{`
        .briefing-md h1 { font-family: var(--serif); font-size: 32px; font-weight: 500;
          line-height: 1.2; margin: 8px 0 12px; color: var(--ink); letter-spacing: -0.01em; }
        .briefing-md h2 { font-family: var(--serif); font-size: 22px; font-weight: 500;
          margin: 32px 0 8px; color: var(--ink); }
        .briefing-md h3 { font-size: 14px; font-weight: 600; text-transform: uppercase;
          letter-spacing: 0.06em; color: var(--ink-2); margin: 24px 0 8px; }
        .briefing-md p { margin: 10px 0; color: var(--ink-2); }
        .briefing-md strong { color: var(--ink); }
        .briefing-md table { width: 100%; border-collapse: collapse; margin: 16px 0;
          font-variant-numeric: tabular-nums; font-size: 13px; }
        .briefing-md th { text-align: left; padding: 8px 10px; font-size: 11px;
          text-transform: uppercase; letter-spacing: 0.05em; color: var(--ink-3);
          border-bottom: 1px solid var(--line); font-weight: 600; }
        .briefing-md td { padding: 8px 10px; border-bottom: 1px solid var(--line);
          color: var(--ink); }
        .briefing-md td:not(:first-child) { text-align: right; }
        .briefing-md th:not(:first-child) { text-align: right; }
        .briefing-md ul { padding-left: 20px; margin: 10px 0; }
        .briefing-md li { margin: 4px 0; color: var(--ink-2); }
        .briefing-md li strong { color: var(--ink); }
        .briefing-md hr { border: none; border-top: 1px solid var(--line); margin: 32px 0; }
        .briefing-md code { font-family: ui-monospace, monospace; font-size: 12px;
          background: var(--paper-2); padding: 2px 5px; border-radius: 3px; }
        .briefing-md .evidence-notes-collapsed { margin-top: 32px; }
        .briefing-md .evidence-notes-collapsed > summary {
          font-family: var(--serif); font-size: 18px; font-weight: 500;
          padding: 12px 14px; cursor: pointer; list-style-position: outside;
          background: var(--paper-2); border: 1px solid var(--line);
          border-radius: 8px; color: var(--ink);
        }
        .briefing-md .evidence-notes-collapsed[open] > summary {
          border-bottom-left-radius: 0; border-bottom-right-radius: 0;
        }
        .briefing-md .evidence-notes-body {
          border: 1px solid var(--line); border-top: none;
          padding: 4px 18px 12px; border-radius: 0 0 8px 8px;
        }
      `}</style>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{beforeMd}</ReactMarkdown>
      {afterMd !== null && (
        <details className="evidence-notes-collapsed">
          <summary>Evidence notes (source versions, collection counts, limitations)</summary>
          <div className="evidence-notes-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{afterMd}</ReactMarkdown>
          </div>
        </details>
      )}
    </div>
  );
};

const GatedTeaser: React.FC<{ redirect: string }> = ({ redirect }) => (
  <div style={{
    padding: '32px', background: palette.paper2, border: `1px solid ${palette.line}`,
    borderRadius: 8, textAlign: 'center',
  }}>
    <div style={{
      fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 500, color: palette.ink,
      marginBottom: 12,
    }}>
      Cross-asset analysis is Premier
    </div>
    <p style={{ color: palette.ink2, fontSize: 15, lineHeight: 1.5, maxWidth: 540, margin: '0 auto 24px' }}>
      Explore the cross-asset analysis, research scenarios, and equity-options
      implications with Premier ($29.99/mo). Public: regime state, prices, and
      Notches readings.
    </p>
    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
      <PurchaseButton
        planKey="premier"
        billing="monthly"
        disabledFallback={
          <PrimaryButton to="/n2/plans#premier">Start Premier · $29.99/mo</PrimaryButton>
        }
      />
      <SecondaryButton to={`/login?redirect=${redirect}`}>Sign in</SecondaryButton>
    </div>
  </div>
);

const ArchiveLockedCard: React.FC<{ date: string; variant: BriefingVariant; redirect: string }> = ({
  date, variant, redirect,
}) => (
  <div style={{
    padding: '32px', background: palette.paper2, border: `1px solid ${palette.line}`,
    borderRadius: 8, textAlign: 'center', marginTop: 24,
  }}>
    <Eyebrow>Archive · {date}</Eyebrow>
    <div style={{
      fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 500, color: palette.ink,
      marginBottom: 12, marginTop: 8,
    }}>
      Historic briefings are Premier
    </div>
    <p style={{ color: palette.ink2, fontSize: 15, lineHeight: 1.5, maxWidth: 540, margin: '0 auto 24px' }}>
      The public feed shows today's briefing plus the last two dates.
      Premier subscribers get the full archive — go back and see how the
      regime + positioning story played out across weeks.
    </p>
    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
      <PurchaseButton
        planKey="premier"
        billing="monthly"
        disabledFallback={
          <PrimaryButton to="/n2/plans#premier">Start Premier · $29.99/mo</PrimaryButton>
        }
      />
      <SecondaryButton to={`/login?redirect=${redirect}`}>Sign in</SecondaryButton>
    </div>
    <div style={{ marginTop: 8 }}>
      <Link to={`/n2/briefings/latest/${variant}`}
        style={{ color: palette.ink3, fontSize: 13, textDecoration: 'underline' }}>
        Read the latest briefing instead →
      </Link>
    </div>
  </div>
);

export default N2BriefingPage;
